import { useCallback, useRef, useState } from 'react';

/**
 * Khoá chống gửi chồng cho các thao tác GHI dữ liệu.
 *
 * ===== VÌ SAO KHÔNG CHỈ DÙNG useState =====
 *
 * Cách hay gặp là: `if (dangGui) return;` với `dangGui` là state. Cách đó KHÔNG
 * chắc chắn. React đặt lại state theo lô và chỉ áp dụng ở lần render kế tiếp,
 * nên hai cú nhấp cách nhau vài mili-giây đều đọc được `dangGui === false` và
 * cùng lọt qua. Nút có mờ đi cũng đã muộn — hai yêu cầu đã rời trình duyệt.
 *
 * `useRef` thì đổi giá trị NGAY lập tức, trong cùng một lượt chạy của handler.
 * Nên khoá thật nằm ở ref; state chỉ để vẽ giao diện.
 *
 * Đây là yêu cầu của UI-01 trong PROJECT_CONTEXT.md:
 *
 *   "Mỗi ý định ghi phải có khóa đồng bộ ngay trong handler (không chỉ debounce
 *    hoặc chờ React render `isSubmitting`)"
 *
 * ===== PHẠM VI =====
 *
 * Khoá theo TỪNG thao tác, không khoá cả màn hình. Người dùng vẫn phải bấm được
 * nút khác trong lúc một việc đang chạy. Muốn khoá nhiều thao tác độc lập thì
 * gọi hook này nhiều lần, mỗi thao tác một khoá riêng.
 *
 * Máy chủ VẪN phải tự chống trùng (DATA-03). Khoá ở giao diện chỉ giảm tai nạn
 * thường gặp, không phải hàng rào bảo mật — người dùng gọi thẳng API thì khoá
 * này vô nghĩa.
 *
 * ===== CÁCH DÙNG =====
 *
 *   const [dangTao, chay] = useKhoaGui();
 *   ...
 *   <NutChinh onClick={() => chay(taoBoSuuTap)} dangChay={dangTao}>Tạo</NutChinh>
 *
 * @returns [dangChay, chay] — `chay` bỏ qua lời gọi mới khi việc cũ chưa xong.
 */
export function useKhoaGui(): [boolean, (viec: () => Promise<unknown>) => Promise<void>] {
  // Khoá THẬT. Đổi ngay trong handler, không chờ render.
  const dangChayRef = useRef(false);
  // Chỉ để vẽ giao diện. Không dùng cái này để quyết định chặn hay không.
  const [dangChay, setDangChay] = useState(false);

  const chay = useCallback(async (viec: () => Promise<unknown>) => {
    if (dangChayRef.current) return;
    dangChayRef.current = true;
    setDangChay(true);
    try {
      await viec();
    } finally {
      // Giải phóng ở MỌI nhánh kết thúc, kể cả khi `viec` ném lỗi. Thiếu finally
      // thì một lần lỗi là nút khoá vĩnh viễn cho tới khi tải lại trang.
      dangChayRef.current = false;
      setDangChay(false);
    }
  }, []);

  return [dangChay, chay];
}
